import type { FC, ReactNode } from 'react'
import type { PublicSettings, ViewerSummary } from '@/types'

import Link from 'next/link'
import { Facebook, Instagram, Mail, MapPin, MessageCircle, Phone } from 'lucide-react'
import { cms } from '@/lib/cms'
import { useI18n } from '@/i18n'
import { Logo } from '@/components/site/logo'
import { MAPS_URL } from '@/constants/site'

interface SiteFooterProps {
    settings: PublicSettings
    viewer: ViewerSummary | null
    today: string
}

const telHref = (value: string): string => `tel:${value.replace(/[^\d+]/g, '')}`

const whatsappHref = (value: string): string => `https://wa.me/${value.replace(/\D/g, '')}`

export const SiteFooter: FC<SiteFooterProps> = ({ settings, viewer, today }): ReactNode => {
    const { t } = useI18n()
    const year = today.slice(0, 4)

    const explore = [
        { href: '/accommodations', label: t.nav.accommodations },
        { href: '/#about', label: t.nav.about },
        { href: '/booking', label: t.nav.booking },
        { href: '/contact', label: t.nav.contact }
    ]

    const service = viewer
        ? [
            { href: '/account', label: t.nav.account },
            { href: '/account/requests', label: t.account.nav.requests },
            { href: '/account/complaints', label: t.account.nav.complaints },
            { href: '/account/messages', label: t.account.nav.messages }
        ]
        : [
            { href: '/login', label: t.nav.login },
            { href: '/register', label: t.nav.register },
            { href: '/booking', label: t.nav.booking }
        ]

    return (
        <footer className='bg-navy text-navy-foreground'>
            <div className='mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr] lg:px-8'>
                <div className='flex flex-col gap-5'>
                    <Logo tone='light' size='lg' />
                    <p className='max-w-sm text-sm leading-relaxed text-navy-foreground/75'>{t.footer.about}</p>

                    {(settings.instagramUrl || settings.facebookUrl) && (
                        <div className='flex gap-2'>
                            {settings.instagramUrl && (
                                <a href={settings.instagramUrl} target='_blank' rel='noopener noreferrer' aria-label='Instagram' className='flex size-10 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold'>
                                    <Instagram className='size-4' aria-hidden='true' />
                                </a>
                            )}
                            {settings.facebookUrl && (
                                <a href={settings.facebookUrl} target='_blank' rel='noopener noreferrer' aria-label='Facebook' className='flex size-10 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold'>
                                    <Facebook className='size-4' aria-hidden='true' />
                                </a>
                            )}
                        </div>
                    )}
                </div>

                <nav aria-label={t.footer.explore}>
                    <h2 className='font-sans text-xs font-bold tracking-[0.2em] text-gold uppercase'>{t.footer.explore}</h2>
                    <ul className='mt-5 flex flex-col gap-3 text-sm'>
                        {explore.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-navy-foreground/80 transition-colors hover:text-white'>{link.label}</Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <nav aria-label={t.footer.service}>
                    <h2 className='font-sans text-xs font-bold tracking-[0.2em] text-gold uppercase'>{t.footer.service}</h2>
                    <ul className='mt-5 flex flex-col gap-3 text-sm'>
                        {service.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='text-navy-foreground/80 transition-colors hover:text-white'>{link.label}</Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div>
                    <h2 className='font-sans text-xs font-bold tracking-[0.2em] text-gold uppercase'>{t.footer.contact}</h2>
                    <ul className='mt-5 flex flex-col gap-4 text-sm'>
                        {settings.address && (
                            <li className='flex gap-3'>
                                <MapPin className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <a href={MAPS_URL} target='_blank' rel='noopener noreferrer' className='text-navy-foreground/80 transition-colors hover:text-white'>
                                    <span {...cms('site_settings', settings.id, 'address')}>{settings.address}</span>
                                </a>
                            </li>
                        )}
                        {settings.contactEmail && (
                            <li className='flex gap-3'>
                                <Mail className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <a href={`mailto:${settings.contactEmail}`} className='break-all text-navy-foreground/80 transition-colors hover:text-white'>
                                    <span {...cms('site_settings', settings.id, 'contact_email')}>{settings.contactEmail}</span>
                                </a>
                            </li>
                        )}
                        {settings.phone && (
                            <li className='flex gap-3'>
                                <Phone className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <a href={telHref(settings.phone)} className='text-navy-foreground/80 transition-colors hover:text-white' dir='ltr'>
                                    <span {...cms('site_settings', settings.id, 'phone')}>{settings.phone}</span>
                                </a>
                            </li>
                        )}
                        {settings.whatsapp && (
                            <li className='flex gap-3'>
                                <MessageCircle className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <a href={whatsappHref(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className='text-navy-foreground/80 transition-colors hover:text-white' dir='ltr'>
                                    <span {...cms('site_settings', settings.id, 'whatsapp')}>{settings.whatsapp}</span>
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className='border-t border-white/10'>
                <div className='mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-xs text-navy-foreground/60 sm:flex-row sm:px-6 lg:px-8'>
                    <p>
                        <span>© {year} </span>
                        <span {...cms('site_settings', settings.id, 'company_name')}>{settings.companyName}</span>
                        <span>. {t.footer.rights}</span>
                    </p>
                    <div className='flex gap-5'>
                        <Link href='/privacy' className='transition-colors hover:text-white'>{t.footer.privacy}</Link>
                        <Link href='/imprint' className='transition-colors hover:text-white'>{t.footer.imprint}</Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}
